import React, { useEffect, useRef } from 'react';
import styles from './Modal.module.scss';

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children: React.ReactNode;
	actions?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, actions }) => {
	const modalRef = useRef<HTMLDivElement>(null);
	const closeBtnRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		// Prevent background scrolling
		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		window.addEventListener('keydown', handleKeyDown);
		closeBtnRef.current?.focus();

		return () => {
			document.body.style.overflow = originalOverflow;
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div className={styles.overlay} onClick={onClose}>
			<div
				ref={modalRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="modal-title"
				className={styles.modal}
				onClick={(e) => e.stopPropagation()}
			>
				<div className={styles.header}>
					<h3 id="modal-title">{title}</h3>
					<button
						ref={closeBtnRef}
						className={styles.closeButton}
						onClick={onClose}
						aria-label="Închide dialogul"
					>
						&times;
					</button>
				</div>
				<div className={styles.content}>
					{children}
				</div>
				{actions && <div className={styles.actions}>{actions}</div>}
			</div>
		</div>
	);
};

export default Modal;
